import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  IconButton,
  useTheme
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  People,
  CreditCard,
  Assessment,
  MoreVert
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'primary', progress }) => {
  const theme = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -4 }}
    >
      <Card
        sx={{
          height: '100%',
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.04)'
            : 'rgba(0, 0, 0, 0.02)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          transition: 'all 0.3s ease-in-out',
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0px 4px 12px rgba(0, 0, 0, 0.3)'
            : '0px 2px 8px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0px 6px 20px rgba(0, 0, 0, 0.4)'
              : '0px 4px 16px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? `${theme.palette[color].main}20`
                  : `${theme.palette[color].main}15`,
                color: `${theme.palette[color].main}`,
              }}
            >
              <Icon fontSize="medium" />
            </Box>
            <IconButton
              size="small"
              sx={{
                color: 'text.secondary',
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.08)'
                }
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            {value}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontWeight: 500 }}>
            {title}
          </Typography>

          {progress !== undefined && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? `${theme.palette[color].main}20`
                    : `${theme.palette[color].main}15`,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: theme.palette[color].main,
                    borderRadius: 3,
                  },
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                {progress}% of target
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {subtitle}
            </Typography>
            {trend && (
              <Chip
                icon={trend === 'up' ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                label={`${trendValue}%`}
                size="small"
                color={trend === 'up' ? 'success' : 'error'}
                variant="outlined"
                sx={{
                  fontSize: '0.75rem',
                  borderColor: trend === 'up' ? 'success.main' : 'error.main',
                  color: trend === 'up' ? 'success.main' : 'error.main',
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.04)'
                    : 'rgba(0, 0, 0, 0.02)'
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const DashboardCards = () => {
  const dashboardData = [
    {
      title: 'Total Members',
      value: '1,247',
      subtitle: 'Active members',
      icon: People,
      trend: 'up',
      trendValue: 12,
      color: 'primary',
      progress: 85,
    },
    {
      title: 'Total Loans',
      value: '₹45.2L',
      subtitle: 'Outstanding amount',
      icon: CreditCard,
      trend: 'up',
      trendValue: 8,
      color: 'success',
      progress: 72,
    },
    {
      title: 'Collections',
      value: '₹12.8L',
      subtitle: 'This month',
      icon: AccountBalance,
      trend: 'down',
      trendValue: 3,
      color: 'warning',
      progress: 65,
    },
    {
      title: 'Pending Approvals',
      value: '23',
      subtitle: 'Loan applications',
      icon: Assessment,
      trend: 'up',
      trendValue: 15,
      color: 'error',
    },
  ];

  return (
    <Grid container spacing={3}>
      {dashboardData.map((card, index) => (
        <Grid item xs={12} sm={6} lg={3} key={index}>
          <StatCard {...card} />
        </Grid>
      ))}
    </Grid>
  );
};

export default DashboardCards;